/* eslint-disable */
import React, {Suspense, useEffect, useRef} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {init as initApm} from "@elastic/apm-rum";
import {useAuth} from "react-oidc-context";
import {isUserAuthenticated, LDAAuthProvider} from "src/auth/LDAAuthProvider";
import ErrorBoundary from "src/layout/ErrorBoundary";
import {
  ChakraProvider,
  Spinner,
  Center,
  Text
} from "@chakra-ui/react";
import theme from "src/theme/chakraTheme";
import "./scss/style.scss";

import { cookieStorageManager } from "@chakra-ui/react";

// Initialize Elastic APM
initApm({
  serviceName: process.env.REACT_APP_OS_APM_SERVICE_NAME,
  serverUrl: process.env.REACT_APP_OS_APM_SERVICE_URL,
  serviceVersion: process.env.REACT_APP_OS_APM_SERVICE_VERSION,
  environment: process.env.REACT_APP_OS_APM_SERVICE_ENVIRONMENT,
});

// Lazy-loaded components
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));
const LoginPage = React.lazy(() => import("./pages/login/login"));

// Reusable loading fallback
const LoadingFallback = () => (
  <Center pt={3}>
    <Spinner size="lg" thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.primary"/>
  </Center>
);

// Protected route wrapper
const ProtectedPage = () => {
  const auth = useAuth();
  const handling = useRef(false);

  useEffect(() => {
    const handleUnauthorized = async () => {
      if (handling.current) return;
      handling.current = true;
      try {
        await auth.signinSilent();
      } catch {
        auth.signinRedirect();
      } finally {
        handling.current = false;
      }
    };
    window.addEventListener('lda:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('lda:unauthorized', handleUnauthorized);
  }, [auth]);

  if (!auth) {
    return <Text>Loading authentication...</Text>;
  }

  if (!isUserAuthenticated(auth)) {
    if (auth.isLoading) {
      return <LoadingFallback/>;
    }
    return <LoginPage isLoading={auth.isLoading} error={auth.error}/>;
  }

  return (
    <Suspense fallback={<LoadingFallback/>}>
      <Routes>
        <Route path="*" element={<DefaultLayout/>}/>
      </Routes>
    </Suspense>
  );
};

// Main App
const App = () => {
  return (
    <ChakraProvider theme={theme} colorModeManager={cookieStorageManager}>
      <ErrorBoundary>
        <LDAAuthProvider>
          <BrowserRouter>
            <ProtectedPage/>
          </BrowserRouter>
        </LDAAuthProvider>
      </ErrorBoundary>
    </ChakraProvider>
  );
};

export default App;
